// Step-by-step guides for entering the report in consumer tax software. Every step says how well it is sourced, and
// the sources are listed with the dates we checked them. Research notes: business-ideas/futures-tax-report/
// research-2026-10-18/filing-software-guides.md in the project files.
import type { ReactNode } from "react";
import { Link } from "../lib/router.tsx";

export const GUIDES_PATH = "/guides";
const CHECKED = "October 18, 2026";

type Level = "official" | "staff" | "community" | "unconfirmed";

const LEVEL: Record<Level, { label: string; title: string }> = {
  official: { label: "From official help", title: "The software maker's own help pages or product pages, or IRS forms" },
  staff: { label: "From vendor staff answers", title: "Answers by the software maker's staff on its community forum, not help articles" },
  community: { label: "From user reports", title: "Reports from other users and independent write-ups, not the software maker" },
  unconfirmed: { label: "Not confirmed", title: "We couldn't confirm this from any source. Check it in the software." },
};

function Sourced({ level }: { level: Level }) {
  return (
    <span className={`src src--${level}`} title={LEVEL[level].title}>
      {LEVEL[level].label}
    </span>
  );
}

function Section(props: { title: string; level: Level; children: ReactNode }) {
  return (
    <section className="card guide-section">
      <div className="guide-section-head">
        <h2 className="card-title">{props.title}</h2>
        <Sourced level={props.level} />
      </div>
      {props.children}
    </section>
  );
}

function Path({ children }: { children: ReactNode }) {
  return <span className="menu-path">{children}</span>;
}

interface Source {
  label: string;
  url: string;
}

function Sources({ list }: { list: Source[] }) {
  return (
    <details className="fine guide-sources">
      <summary>Sources, checked {CHECKED}</summary>
      <ul>
        {list.map((s) => (
          <li key={s.url}>
            <a href={s.url} rel="noopener noreferrer" target="_blank">
              {s.label}
            </a>
          </li>
        ))}
      </ul>
    </details>
  );
}

export interface Guide {
  slug: string;
  path: string;
  name: string;
  title: string;
  h1: string;
  lede: string;
  confidence: string;
  render: () => ReactNode;
}

/** What holds in every product: the figures come from the report, and these rules decide how they go in. */
function BeforeYouStart() {
  return (
    <section className="card guide-section">
      <h2 className="card-title">Before you start</h2>
      <ul className="plain-list">
        <li>
          Have your <Link href="/organizer">Futures Tax Report</Link> open on the Report step. Every amount you type comes from it, and it tells you which
          form each one goes on.
        </li>
        <li>
          <strong>Futures:</strong> enter box 11 of each 1099-B, one entry per broker. Box 11 already includes boxes 8, 9 and 10, so never enter those as
          well, and don't also enter the futures as stock sales.
        </li>
        <li>
          <strong>Prop payouts:</strong> each payout counts once, however many forms show it. A payout on both a 1099-NEC and a 1099-K is still one
          payout.
        </li>
        <li>
          <strong>Payouts with no 1099 are still income.</strong> For payments made in 2026, a firm only has to send a 1099-NEC or 1099-MISC once it pays
          you $2,000 or more in the year (it was $600), so more payouts will arrive without a form.
        </li>
        <li>
          <strong>Business code:</strong> the IRS has no guidance on which code fits prop-firm payouts. Many traders use 523900, "Other financial
          investment activities." Two codes some blogs suggest, 523910 and 523130, aren't on the IRS list.
        </li>
        <li>
          <strong>Fees:</strong> no product has a category for evaluation, reset or data fees. They're usually entered as "Other expenses" with a short
          description, such as "Prop firm evaluation fees."
        </li>
        <li>
          <strong>Loss carryback:</strong> leave the "net section 1256 contracts loss election" (Form 6781 box D) unchecked unless you mean to carry a
          futures loss back to earlier years. None of these products files the earlier-year claims for you, so talk to a preparer first.
        </li>
      </ul>
    </section>
  );
}

function CheckBeforeFiling({ preview }: { preview: ReactNode }) {
  return (
    <section className="card guide-section">
      <h2 className="card-title">Check before you file</h2>
      <p className="card-intro">{preview}</p>
      <ul className="plain-list">
        <li>Form 6781 lines 7, 8 and 9 match your report's Form 6781 card.</li>
        <li>Schedule D lines 4 and 11 show the same two amounts as Form 6781 lines 8 and 9.</li>
        <li>Schedule C line 1 matches your report's gross receipts, and line 31 its net profit (before any home office deduction).</li>
        <li>Schedule SE is close to the cross-check in your report. A few dollars of rounding is normal.</li>
        <li>Any box 4 withholding appears on Form 1040 line 25b.</li>
      </ul>
      <p className="field-hint">If something doesn't match, don't change your 1099s to make it fit. Find which entry is off, or ask a preparer.</p>
    </section>
  );
}

function YearNote({ extra }: { extra?: ReactNode }) {
  return (
    <div className="callout callout--warn">
      <strong>Written for 2026 returns, filed in 2027.</strong> The 2026 versions of the software aren't out yet, so menu names come from the 2025
      versions, which usually change little. We'll recheck them when the 2026 versions ship. {extra}
    </div>
  );
}

function TurboTax() {
  return (
    <>
      <YearNote />
      <BeforeYouStart />
      <Section title="Which version you need" level="official">
        <ul className="plain-list">
          <li>
            <strong>TurboTax Online: Premium.</strong> Intuit lists Form 6781, Schedule D and the 1099-B as not included in Deluxe, and Deluxe's Schedule C
            is income only, with no expenses. The Free Edition covers neither.
          </li>
          <li>
            <strong>TurboTax Desktop:</strong> user reports say every Desktop edition has the same forms, Deluxe included. Intuit's comparison doesn't say
            so plainly, and it markets Premier for investments and Home & Business for the self-employed.
          </li>
        </ul>
      </Section>
      <Section title="Futures: Form 6781" level="staff">
        <ol className="guide-steps">
          <li>
            Online: <Path>Federal › Wages & Income › Investment Income (or Less Common Investments and Savings) › Contracts & Straddles</Path>. Desktop:{" "}
            <Path>Personal Income › Investment Income › Contracts & Straddles</Path>, or search "form 6781" and choose Jump to. The heading has moved
            between years.
          </li>
          <li>Answer Yes to "Any straddles or Section 1256 contracts?"</li>
          <li>On the elections screen, leave every box blank unless you mean to make that election. Box D is the loss carryback.</li>
          <li>
            Check only <strong>Section 1256 contracts marked to market</strong>, and choose to report totals for each broker or account.
          </li>
          <li>
            For each 1099-B, enter the broker's name and box 11. A gain goes in <strong>Total profits</strong>. A loss goes in{" "}
            <strong>Total losses, as a positive number</strong>.
          </li>
          <li>Leave special handling at "None of the above." Add the next broker the same way.</li>
        </ol>
        <p className="field-hint">
          <strong>If you imported your 1099-B:</strong> imports don't bring in the Section 1256 figures, so enter box 11 yourself. If the import put your
          futures under stocks, delete them there, or they're counted twice.
        </p>
      </Section>
      <Section title="Prop payouts: Schedule C" level="official">
        <ol className="guide-steps">
          <li>
            Online: <Path>Federal › Income › Self-employment</Path>. Desktop:{" "}
            <Path>Federal Taxes › Wages & Income › Business Items › Business Income and Expenses (Sch C)</Path>.
          </li>
          <li>Answer Yes to "Did you have any self-employment income or expenses?" and describe the work. Pick your business code.</li>
          <li>
            At "Let's enter the income for your work," choose <strong>Form 1099-NEC</strong> and enter each one as printed. Entering it inside the work
            ties it to this Schedule C. A 1099-NEC entered on its own shows a "link to Schedule C" error.
          </li>
          <li>
            Add payouts that came with no 1099 as <strong>Other self-employed income</strong>, so the total matches your report's gross receipts.
          </li>
          <li>
            Enter your fees under the work's <strong>Expenses</strong> (evaluation, reset and data fees as other miscellaneous expenses, with a
            description).
          </li>
        </ol>
      </Section>
      <Section title="1099-K and 1099-MISC" level="staff">
        <ul className="plain-list">
          <li>
            <strong>A 1099-K for payouts already on a 1099-NEC:</strong> enter the 1099-K, choose "This amount in box 1a is too high or includes some
            personal transactions," and enter the duplicated amount as a positive number. Or skip it and enter that income only once, as other
            self-employed income.
          </li>
          <li>
            <strong>A 1099-K with no other form:</strong> in the Schedule C income section, choose <Path>Add income for this work › Form 1099-K</Path>.
          </li>
          <li>
            <strong>A 1099-MISC (box 3):</strong> <Path>Wages & Income › Other Common Income › Form 1099-MISC</Path>. Answer Yes to "Did this involve an
            intent to earn money?" so it goes to Schedule C rather than other income.
          </li>
        </ul>
      </Section>
      <Section title="Tax withheld (box 4)" level="community">
        <ul className="plain-list">
          <li>
            On 1099-NEC and 1099-MISC screens, box 4 is a field on the form. Some screens show it only after you check "My form has more info or boxes."
          </li>
          <li>
            For a 1099-K, use the box 4 field if the screen has one. If not, an older answer points to{" "}
            <Path>Deductions & Credits › Estimates and Other Taxes Paid › Other Income Taxes › Withholding not already entered</Path>. Enter it one way only.
          </li>
        </ul>
      </Section>
      <CheckBeforeFiling preview={<>Online, preview your forms under Tax Tools › Print Center. Desktop, switch to Forms view.</>} />
      <Sources
        list={[
          { label: "TurboTax Premium: forms included and not included in Deluxe", url: "https://turbotax.intuit.com/personal-taxes/online/premium/" },
          { label: "Intuit: How do I report income from self-employment? (updated July 10, 2026)", url: "https://ttlc.intuit.com/turbotax-support/en-us/help-article/self-employment-taxes/report-income-self-employment/L5wADi3xQ_US_en_US" },
          { label: "Intuit: Where do I enter Schedule C? (updated April 20, 2026)", url: "https://ttlc.intuit.com/turbotax-support/en-us/help-article/import-export-data-files/enter-schedule-c/L5Fz3j5us_US_en_US" },
          { label: "Intuit: How do I enter a 1099-K? (updated August 10, 2026)", url: "https://ttlc.intuit.com/turbotax-support/en-us/help-article/import-export-data-files/enter-1099-k-turbotax-online/L3D9hqZzr_US_en_US" },
          { label: "Intuit staff: entering Section 1256 contracts from a 1099-B (March 2026)", url: "https://ttlc.intuit.com/community/get-your-taxes-done-using-turbotax-6/how-to-enter-section-1256-option-contracts-at-the-bottom-on-form-1099-b-138834" },
          { label: "Intuit staff: where to enter Section 1256 transactions (March 2025)", url: "https://ttlc.intuit.com/community/business-taxes/discussion/where-do-i-enter-section-1256-transactions-regulated-future-contracts-and-options/00/3551816" },
          { label: "Intuit staff: Section 1256 figures don't import (March 2025)", url: "https://ttlc.intuit.com/community/taxes/discussion/i-got-1099-b-from-robinhood-in-summary-information-section-1256-contracts-with-a-loss-but-after/00/3511307" },
          { label: "Intuit staff: removing income on both a 1099-NEC and a 1099-K (March 2025)", url: "https://ttlc.intuit.com/community/taxes/discussion/income-in-1099-nec-that-is-also-reported-in-1099-k-how-to-remove-duplicate-income/00/3570242" },
          { label: "Intuit staff: entering Schedule C expenses for 2025 (April 2026)", url: "https://ttlc.intuit.com/community/business-farm-10/how-to-enter-expenses-on-schedule-c-for-2025-836880" },
          { label: "Intuit staff: 1099-MISC and \"intent to earn money\" (February 2025)", url: "https://ttlc.intuit.com/community/self-employed-group/discussion/how-do-i-enter-a1099-misc-as-other-income-not-self-employment-income/00/3473548" },
          { label: "IRS Form 6781 and instructions", url: "https://www.irs.gov/pub/irs-pdf/f6781.pdf" },
        ]}
      />
    </>
  );
}

function HrBlock() {
  return (
    <>
      <YearNote extra={<>H&R Block publishes very little step-by-step help on these forms, so more of this guide is unconfirmed than the others.</>} />
      <BeforeYouStart />
      <Section title="Which version you need" level="official">
        <ul className="plain-list">
          <li>
            <strong>Prop payouts with expenses</strong> need <strong>Self-Employed Online</strong>. Deluxe and Premium Online cover Schedule C income only.
          </li>
          <li>
            <strong>Futures (Form 6781) Online is unclear.</strong> H&R Block's general list of supported forms includes Form 6781, but the form lists
            for Premium and Self-Employed don't show it. Before paying, check that you can reach the Section 1256 screen.
          </li>
          <li>
            <strong>H&R Block Desktop</strong> lists Form 6781 for Windows and Mac. Premium & Business is the edition for business owners. We couldn't
            confirm which edition unlocks each form.
          </li>
        </ul>
      </Section>
      <Section title="Futures: Form 6781" level="community">
        <ol className="guide-steps">
          <li>
            Go to <Path>Federal › Income › Investments › Section 1256 Contracts and Straddles</Path>.
          </li>
          <li>Answer Yes to the question about contracts. One write-up warns that No ends this section.</li>
          <li>
            If the interview doesn't ask for the figures, open the form itself (Desktop's whole-form view) and fill line 1: the broker's name in column
            (a), a loss in column (b) as a positive number, and a gain in column (c). One row per 1099-B.
          </li>
          <li>The other lines fill in on their own. Check them against your report.</li>
        </ol>
        <p className="field-hint">Whether the interview asks about the loss carryback (box D) isn't confirmed. Leave it unchecked.</p>
      </Section>
      <Section title="Prop payouts: Schedule C" level="unconfirmed">
        <ul className="plain-list">
          <li>Start a business (Schedule C) in the income section, pick your business code, and enter each 1099-NEC as printed under that business.</li>
          <li>Add payouts that came with no 1099 as other business income, so the total matches your report's gross receipts.</li>
          <li>Enter fees in the business's expenses, as other expenses with a description.</li>
        </ul>
        <p className="field-hint">H&R Block doesn't publish these screens. The steps follow how its Schedule C works generally.</p>
      </Section>
      <Section title="1099-K and 1099-MISC" level="official">
        <ul className="plain-list">
          <li>
            Business income on a 1099-K goes on Schedule C line 1. If the same payouts are also on a 1099-NEC, H&R Block says "you only need to report
            the income once."
          </li>
          <li>Its software combines several 1099-Ks for you, with the amounts withheld. How it backs out a duplicate isn't published.</li>
          <li>A business 1099-MISC (box 3) belongs on Schedule C, not other income.</li>
        </ul>
      </Section>
      <Section title="Tax withheld (box 4)" level="unconfirmed">
        <p className="card-intro">
          H&R Block says backup withholding is reported on your return and that 1099-K withholding is combined for you, but not which screen it's on.
          Look for a box 4 field on each form's entry screen, enter it once, and check Form 1040 line 25b.
        </p>
      </Section>
      <CheckBeforeFiling preview={<>Preview your return as forms before you file.</>} />
      <Sources
        list={[
          { label: "H&R Block Online: Self-Employed forms list", url: "https://www.hrblock.com/online-tax-filing/self-employed-online-tax-filing/federal-tax-forms/" },
          { label: "H&R Block Online: Premium forms list", url: "https://www.hrblock.com/online-tax-filing/premium-online-tax-filing/federal-tax-forms/" },
          { label: "H&R Block: Which forms are supported by H&R Block Online?", url: "https://www.hrblock.com/tax-center/support/online/online-tax-filing/prepare-to-file-taxes-online/supported-forms/" },
          { label: "H&R Block Desktop: federal forms available", url: "https://www.hrblock.com/tax-software/updates-back-editions/federal-forms-available.html" },
          { label: "H&R Block: What is a 1099-K tax form? (May 21, 2026)", url: "https://www.hrblock.com/tax-center/irs/forms/what-is-form-1099-k/" },
          { label: "H&R Block: Form 1099-NEC (December 10, 2024)", url: "https://www.hrblock.com/tax-center/irs/forms/form-1099-nec/" },
          { label: "The Finance Buff: entering Section 1256 trades in TurboTax, H&R Block and FreeTaxUSA (2024)", url: "https://thefinancebuff.com/box-spread-trade-taxes.html" },
          { label: "IRS Form 6781 and instructions", url: "https://www.irs.gov/pub/irs-pdf/f6781.pdf" },
        ]}
      />
    </>
  );
}

function FreeTaxUsa() {
  return (
    <>
      <YearNote />
      <BeforeYouStart />
      <Section title="Which version you need" level="official">
        <p className="card-intro">
          The free federal return covers both Form 6781 and Schedule C, with Schedule SE. Deluxe adds live chat and unlimited amended returns but no extra forms. State
          returns cost extra.
        </p>
      </Section>
      <Section title="Futures: Form 6781" level="official">
        <ol className="guide-steps">
          <li>
            Go to <Path>Income › Uncommon Income › Contracts and Straddles</Path> and answer Yes.
          </li>
          <li>Leave the elections blank unless you mean to make one. The net section 1256 contracts loss election is the carryback.</li>
          <li>
            Choose <strong>Add a Section 1256 contract</strong> and enter the broker's name and box 11. Add one entry for each 1099-B.
          </li>
          <li>
            <strong>Type a loss with a minus sign</strong>, like -2,000. FreeTaxUSA treats a number without one as a gain.
          </li>
          <li>Answer No to the straddle question and to the question about unrealized gains. Box 11 already includes open positions at year-end.</li>
        </ol>
      </Section>
      <Section title="Prop payouts: Schedule C" level="official">
        <ol className="guide-steps">
          <li>
            Go to <Path>Income › Business / Rental Income › Business Income (Schedule C)</Path>. On Basic Business Information, describe the work, pick
            your business code, and choose Cash as the accounting method.
          </li>
          <li>
            On <strong>Link 1099-NEC or 1099-MISC</strong>, check each prop firm's 1099-NEC, or answer Yes to add one. A linked 1099-NEC flows to
            Schedule C on its own.
          </li>
          <li>
            Under gross receipts, enter only payouts that were <strong>not</strong> on a 1099-NEC or 1099-MISC. Entering a linked 1099-NEC again here
            doubles it.
          </li>
          <li>Enter fees on the expense screens, as other expenses with a description.</li>
        </ol>
      </Section>
      <Section title="1099-K and 1099-MISC" level="staff">
        <ul className="plain-list">
          <li>
            A business 1099-K goes in <Path>Business Income (Schedule C)</Path>. If it covers payouts already on a linked 1099-NEC, don't report them
            again. Report only the part of the 1099-K that's not on another form.
          </li>
          <li>For a 1099-MISC (box 3) from a prop firm, choose Schedule C on the screen after you enter it, not other income.</li>
        </ul>
      </Section>
      <Section title="Tax withheld (box 4)" level="official">
        <ul className="plain-list">
          <li>
            1099-K box 4 goes on <Path>Miscellaneous Forms and Topics › Extension and Other Tax Payments</Path>.
          </li>
          <li>
            If a 1099-NEC had tax withheld, enter the form itself rather than typing the income in directly, so the withholding is captured. Check
            Form 1040 line 25b.
          </li>
        </ul>
      </Section>
      <CheckBeforeFiling preview={<>Use the "view PDF" link for Contracts and Straddles on the income summary, then preview the whole return.</>} />
      <Sources
        list={[
          { label: "FreeTaxUSA: supported forms (2025)", url: "https://www.freetaxusa.com/forms/" },
          { label: "FreeTaxUSA: where to enter Form 6781", url: "https://www.freetaxusa.com/answer/4301/Where-do-I-enter-Gains-and-Losses-From-Section-1256-Contracts-and-Straddles-Form-6781/" },
          { label: "FreeTaxUSA: where to enter Form 1099-B, box 11", url: "https://www.freetaxusa.com/answer/6107/Where-do-I-enter-Form-1099B-Box-11/" },
          { label: "FreeTaxUSA: link a 1099-NEC to a business", url: "https://www.freetaxusa.com/answer/8992/How-do-I-link-my-1099NEC-to-a-business-Schedule-C/" },
          { label: "FreeTaxUSA: 1099-NEC income doubled on Schedule C", url: "https://www.freetaxusa.com/answer/8998/The-1099NEC-income-is-doubled-on-my-business-schedule-C/" },
          { label: "FreeTaxUSA: entering a 1099-NEC", url: "https://www.freetaxusa.com/answer/3558/Nonemployee-Compensation-Form-1099NEC/" },
          { label: "FreeTaxUSA: where to enter a 1099-K", url: "https://www.freetaxusa.com/answer/4182/Where-do-I-enter-my-Form-1099K/" },
          { label: "FreeTaxUSA: 1099-MISC box 3", url: "https://www.freetaxusa.com/answer/7226/Where-do-I-report-the-1099MISC-Box-3-amount/" },
          { label: "FreeTaxUSA community: a futures loss counted as a gain", url: "https://community.freetaxusa.com/discussion/3359/futures-contract-loss-on-1099-b-line-11-captured-as-gain-on-form-6781" },
          { label: "IRS Form 6781 and instructions", url: "https://www.irs.gov/pub/irs-pdf/f6781.pdf" },
        ]}
      />
    </>
  );
}

export const GUIDES: Guide[] = [
  {
    slug: "turbotax",
    path: `${GUIDES_PATH}/turbotax`,
    name: "TurboTax",
    title: "How to Enter Futures and Prop Firm Income in TurboTax (2026)",
    h1: "Entering futures and prop payouts in TurboTax",
    lede: "Where Form 6781 and your prop-firm Schedule C go in TurboTax Online and Desktop, which edition you need, and the import and double-counting traps.",
    confidence: "Mostly from Intuit's help pages and staff answers",
    render: () => <TurboTax />,
  },
  {
    slug: "hr-block",
    path: `${GUIDES_PATH}/hr-block`,
    name: "H&R Block",
    title: "How to Enter Futures and Prop Firm Income in H&R Block (2026)",
    h1: "Entering futures and prop payouts in H&R Block",
    lede: "Which H&R Block product handles Form 6781 and Schedule C expenses, and how to enter your report's figures. H&R Block documents less than the others, so we mark what we couldn't confirm.",
    confidence: "Partly confirmed: H&R Block publishes little step-by-step help",
    render: () => <HrBlock />,
  },
  {
    slug: "freetaxusa",
    path: `${GUIDES_PATH}/freetaxusa`,
    name: "FreeTaxUSA",
    title: "How to Enter Futures and Prop Firm Income in FreeTaxUSA (2026)",
    h1: "Entering futures and prop payouts in FreeTaxUSA",
    lede: "FreeTaxUSA's free federal return covers Form 6781 and Schedule C. Here's where each figure from your report goes, and the minus sign that trips people up.",
    confidence: "Mostly from FreeTaxUSA's own help pages",
    render: () => <FreeTaxUsa />,
  },
];

export function GuidesIndex() {
  return (
    <>
      <div className="tools guide-list">
        {GUIDES.map((g) => (
          <Link key={g.slug} href={g.path} className="tool">
            <span className="tool-name">{g.name}</span>
            <span className="tool-body">{g.confidence}</span>
            <span className="tool-go">Open the guide →</span>
          </Link>
        ))}
      </div>
      <BeforeYouStart />
      <p className="field-hint">We never file for you. These guides show where your report's figures go; you enter and check them yourself.</p>
    </>
  );
}

/** The other guides, for the foot of each one. */
export function OtherGuides({ current }: { current: string }) {
  return (
    <p className="guide-others">
      Using something else?{" "}
      {GUIDES.filter((g) => g.slug !== current).map((g, i) => (
        <span key={g.slug}>
          {i > 0 && " · "}
          <Link href={g.path}>{g.name}</Link>
        </span>
      ))}
    </p>
  );
}
